import React, { useMemo, useState, createElement } from 'react';
import { Users, Search, Filter, Download, Eye, Ban, UserCheck, MessageSquare, AlertTriangle, CreditCard, ShoppingBag, MoreVertical, Video, RefreshCw, Star, Calendar, MapPin, Clock, Mail, Phone, Edit3, Tag, ChevronDown, X, CheckSquare, Square, FileText, Activity, ThumbsUp, Zap } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { Input } from '../ui/Input';
import { Tabs } from '../ui/Tabs';
interface AdminUsersManagerProps {
  users: any[];
  orders: any[];
  astrologers: any[];
  setActiveTab: (tab: string) => void;
  setDetailPanel: (panel: any) => void;
  setCreatePanel?: (panel: any) => void;
  success: (msg: string) => void;
}
export function AdminUsersManager({
  users: initialUsers,
  orders,
  astrologers,
  setActiveTab,
  setDetailPanel: setExternalDetailPanel,
  setCreatePanel,
  success
}: AdminUsersManagerProps) {
  // Enhance users with mock data for new fields if they don't exist
  const [users, setUsers] = useState(() => initialUsers.map(u => ({
    ...u,
    lastLogin: u.lastLogin || '2 часа назад',
    country: u.country || ['Россия', 'Беларусь', 'Казахстан', 'Украина'][Math.floor(Math.random() * 4)],
    language: u.language || 'Русский',
    consultations: u.consultations !== undefined ? u.consultations : Math.floor(Math.random() * 5),
    lastOrder: u.lastOrder || u.date,
    notes: u.notes || '',
    riskFlag: u.riskFlag || null,
    isVip: u.isVip || parseInt(u.spent?.replace(/\D/g, '') || '0') > 30000,
    hasComplaints: u.hasComplaints || Math.random() > 0.8,
    hasActiveChats: u.hasActiveChats || Math.random() > 0.7,
    hasPaymentIssues: u.hasPaymentIssues || Math.random() > 0.9,
    phone: u.phone || '+7 (999) 000-00-00',
    refunds: Math.random() > 0.8 ? 1 : 0
  })));
  // --- STATES ---
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  // Detail Panel State (Internal to handle tabs and rich data)
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  // Action Modals State
  const [noteModal, setNoteModal] = useState<{
    userId: number;
    text: string;
  } | null>(null);
  const [riskModal, setRiskModal] = useState<{
    userId: number;
    flag: string;
  } | null>(null);
  const [actionMenuOpen, setActionMenuOpen] = useState<number | null>(null);
  // Filter State
  const [filters, setFilters] = useState({
    status: 'all',
    subscription: 'all',
    ordersMin: '',
    ordersMax: '',
    spentMin: '',
    spentMax: '',
    dateFrom: '',
    dateTo: '',
    country: 'all',
    language: 'all',
    hasComplaints: 'all',
    hasActiveOrders: 'all',
    hasConsultations: 'all'
  });
  const itemsPerPage = 15;
  // --- FILTERING LOGIC ---
  const filteredUsers = useMemo(() => {
    return users.filter(u => {
      // Search
      const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) || u.email.toLowerCase().includes(searchQuery.toLowerCase()) || u.id.toString().includes(searchQuery) || u.phone && u.phone.includes(searchQuery);
      if (!matchesSearch) return false;
      // Advanced Filters
      if (filters.status !== 'all' && u.status !== filters.status) return false;
      if (filters.subscription !== 'all') {
        const hasSub = !!u.subscription;
        if (filters.subscription === 'true' && !hasSub) return false;
        if (filters.subscription === 'false' && hasSub) return false;
      }
      const ordersCount = parseInt(u.orders) || 0;
      if (filters.ordersMin && ordersCount < parseInt(filters.ordersMin)) return false;
      if (filters.ordersMax && ordersCount > parseInt(filters.ordersMax)) return false;
      const spentAmount = parseInt(u.spent?.replace(/\D/g, '') || '0');
      if (filters.spentMin && spentAmount < parseInt(filters.spentMin)) return false;
      if (filters.spentMax && spentAmount > parseInt(filters.spentMax)) return false;
      if (filters.country !== 'all' && u.country !== filters.country) return false;
      if (filters.language !== 'all' && u.language !== filters.language) return false;
      if (filters.hasComplaints !== 'all') {
        if (filters.hasComplaints === 'true' && !u.hasComplaints) return false;
        if (filters.hasComplaints === 'false' && u.hasComplaints) return false;
      }
      if (filters.hasConsultations !== 'all') {
        const hasCons = (u.consultations || 0) > 0;
        if (filters.hasConsultations === 'true' && !hasCons) return false;
        if (filters.hasConsultations === 'false' && hasCons) return false;
      }
      return true;
    });
  }, [users, searchQuery, filters]);
  // --- PAGINATION ---
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const currentUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // --- KPIs ---
  const kpis = useMemo(() => {
    return {
      total: users.length,
      newToday: 12,
      active: users.filter(u => u.status === 'active').length,
      blocked: users.filter(u => u.status === 'blocked').length,
      withSub: users.filter(u => !!u.subscription).length,
      withComplaints: users.filter(u => u.hasComplaints).length,
      pendingOrders: users.filter(u => u.orders > 0 && Math.random() > 0.7).length,
      refunds: users.filter(u => u.refunds > 0).length,
      activeConsultations: users.filter(u => u.consultations > 0 && Math.random() > 0.8).length,
      vip: users.filter(u => u.isVip).length
    };
  }, [users]);
  // --- HANDLERS ---
  const handleExport = () => {
    const headers = ['ID', 'Имя', 'Email', 'Телефон', 'Дата регистрации', 'Страна', 'Заказы', 'Консультации', 'Потрачено', 'Подписка', 'Статус'];
    const rows = filteredUsers.map(u => [u.id, u.name, u.email, u.phone || '', u.date, u.country, u.orders, u.consultations, u.spent, u.subscription || '—', u.status === 'active' ? 'Активен' : 'Заблокирован']);
    const csvContent = [headers.join(','), ...rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    success(`Экспорт ${filteredUsers.length} пользователей завершён`);
  };
  const handleToggleBlock = (userId: number) => {
    setUsers(users.map(u => {
      if (u.id === userId) {
        const newStatus = u.status === 'active' ? 'blocked' : 'active';
        success(newStatus === 'active' ? `${u.name} разблокирован` : `${u.name} заблокирован`);
        return {
          ...u,
          status: newStatus
        };
      }
      return u;
    }));
    setActionMenuOpen(null);
  };
  const handleSaveNote = () => {
    if (!noteModal) return;
    setUsers(users.map(u => u.id === noteModal.userId ? {
      ...u,
      notes: noteModal.text
    } : u));
    success('Заметка сохранена');
    setNoteModal(null);
    setActionMenuOpen(null);
  };
  const handleSaveRisk = () => {
    if (!riskModal) return;
    setUsers(users.map(u => u.id === riskModal.userId ? {
      ...u,
      riskFlag: riskModal.flag || null
    } : u));
    success('Risk-флаг обновлен');
    setRiskModal(null);
    setActionMenuOpen(null);
  };
  const toggleSelectAll = () => {
    if (selectedUsers.size === currentUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(currentUsers.map(u => u.id)));
    }
  };
  const toggleSelectUser = (id: number) => {
    const newSet = new Set(selectedUsers);
    if (newSet.has(id)) newSet.delete(id);else newSet.add(id);
    setSelectedUsers(newSet);
  };
  const handleMassAction = (action: string) => {
    if (selectedUsers.size === 0) return;
    switch (action) {
      case 'block':
        setUsers(users.map(u => selectedUsers.has(u.id) ? {
          ...u,
          status: 'blocked'
        } : u));
        success(`${selectedUsers.size} пользователей заблокировано`);
        break;
      case 'email':
        success(`Открыта форма рассылки для ${selectedUsers.size} пользователей`);
        break;
      case 'export':
        {
          const headers = ['ID', 'Имя', 'Email', 'Телефон', 'Статус'];
          const selectedArr = users.filter(u => selectedUsers.has(u.id));
          const rows = selectedArr.map(u => [u.id, u.name, u.email, u.phone || '', u.status === 'active' ? 'Активен' : 'Заблокирован']);
          const csvContent = [headers.join(','), ...rows.map(r => r.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
          const blob = new Blob(['\uFEFF' + csvContent], {
            type: 'text/csv;charset=utf-8;'
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `users_selected_${new Date().toISOString().slice(0, 10)}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          success(`Экспорт ${selectedUsers.size} выбранных пользователей завершён`);
        }
        break;
      case 'tag':
        success(`Тег добавлен ${selectedUsers.size} пользователям`);
        break;
    }
    setSelectedUsers(new Set());
  };
  const applyKpiFilter = (filterType: string) => {
    // Reset filters first
    const newFilters = {
      status: 'all',
      subscription: 'all',
      ordersMin: '',
      ordersMax: '',
      spentMin: '',
      spentMax: '',
      dateFrom: '',
      dateTo: '',
      country: 'all',
      language: 'all',
      hasComplaints: 'all',
      hasActiveOrders: 'all',
      hasConsultations: 'all'
    };
    switch (filterType) {
      case 'active':
        newFilters.status = 'active';
        break;
      case 'blocked':
        newFilters.status = 'blocked';
        break;
      case 'sub':
        newFilters.subscription = 'true';
        break;
      case 'complaints':
        newFilters.hasComplaints = 'true';
        break;
      case 'consultations':
        newFilters.hasConsultations = 'true';
        break;
      case 'vip':
        newFilters.spentMin = '30000';
        break;
      case 'pendingOrders':
        newFilters.hasActiveOrders = 'true';
        break;
      case 'refunds':
        newFilters.status = 'all';
        break;
    }
    setFilters(newFilters);
    setCurrentPage(1);
  };
  // --- RENDERERS ---
  const renderKpiCard = (label: string, value: string | number, icon: React.ReactNode, bgClass: string, textClass: string, filterKey?: string) => <Card padding="sm" hoverable className="rounded-xl shadow-none border-slate-200 p-4 flex flex-col justify-between h-24 cursor-pointer group" onClick={() => filterKey ? applyKpiFilter(filterKey) : applyKpiFilter('all')} data-id="element-6404">
      <div className="flex justify-between items-start" data-id="element-6405">
        <div className={`w-8 h-8 rounded-lg ${bgClass} flex items-center justify-center group-hover:scale-110 transition-transform`} data-id="element-6406">
          {icon}
        </div>
      </div>
      <div data-id="element-6407">
        <div className="text-xs text-slate-500 font-medium mb-0.5" data-id="element-6408">{label}</div>
        <div className="text-xl font-bold text-slate-900" data-id="element-6409">{value}</div>
      </div>
    </Card>;
  const renderUserDetailPanel = () => {
    if (!selectedUser) return null;
    const mockReports = [{
      id: 1,
      type: 'Натальная карта',
      date: selectedUser.lastOrder || '10 Окт 2024',
      status: 'ready'
    }, {
      id: 2,
      type: 'Совместимость',
      date: '05 Окт 2024',
      status: 'ready'
    }, {
      id: 3,
      type: 'Прогноз транзитов',
      date: '01 Окт 2024',
      status: 'processing'
    }];
    const mockConsultations = [{
      id: 1,
      type: 'Видео-консультация',
      astrologer: 'Мария Звёздная',
      duration: '45 мин',
      date: '08 Окт 2024',
      status: 'completed'
    }, {
      id: 2,
      type: 'Чат-консультация',
      astrologer: 'Павел Орион',
      duration: '30 мин',
      date: '25 Сен 2024',
      status: 'completed'
    }];
    const mockChats = [{
      id: 1,
      with: 'Мария Звёздная',
      role: 'Астролог',
      lastMsg: 'Спасибо за консультацию! Подскажите ещё...',
      time: '10 мин назад',
      unread: 1
    }, {
      id: 2,
      with: 'Поддержка',
      role: 'Модератор',
      lastMsg: 'Ваш вопрос решён. Обращайтесь!',
      time: 'Вчера',
      unread: 0
    }];
    const mockOrders = [...Array(Math.min(parseInt(selectedUser.orders) || 0, 5))].map((_, i) => ({
      id: 1000 + i,
      product: ['Натальная карта', 'Совместимость', 'Прогноз транзитов', 'Солнечный возврат', 'Комбо-анализ'][i % 5],
      price: [3900, 2900, 4500, 3200, 6900][i % 5],
      date: selectedUser.lastOrder || '10 Окт 2024',
      status: i === 0 ? 'processing' : 'completed'
    }));
    const tabs = [{
      id: 'basic',
      label: 'Основное',
      content: <div className="space-y-6" data-id="element-6410">
            <div className="flex items-start gap-4" data-id="element-6411">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-2xl" data-id="element-6412">
                {selectedUser.name.split(' ').map((n: string) => n[0]).join('')}
              </div>
              <div className="flex-1" data-id="element-6413">
                <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-6414">
                  {selectedUser.name}
                  {selectedUser.isVip && <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-6415" />}
                </h2>
                <p className="text-sm text-slate-500 mb-2" data-id="element-6416">
                  {selectedUser.email} • ID: {selectedUser.id}
                </p>
                <div className="flex gap-2" data-id="element-6417">
                  <Badge variant={selectedUser.status === 'active' ? 'success' : 'error'} data-id="element-6418">
                    {selectedUser.status === 'active' ? 'Активен' : 'Заблокирован'}
                  </Badge>
                  {selectedUser.subscription && <Badge variant="pro" data-id="element-6419">{selectedUser.subscription}</Badge>}
                </div>
              </div>
            </div>

            {/* Quick Navigation */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-id="element-6420">
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('orders');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group" data-id="element-6421">
                <ShoppingBag className="w-5 h-5 text-slate-400 group-hover:text-indigo-600 transition-colors" data-id="element-6422" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-indigo-700" data-id="element-6423">
                  Заказы ({selectedUser.orders})
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('full_chat');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-colors group" data-id="element-6424">
                <MessageSquare className="w-5 h-5 text-slate-400 group-hover:text-blue-600 transition-colors" data-id="element-6425" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-blue-700" data-id="element-6426">
                  Чаты
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('ai_reports');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-purple-300 hover:bg-purple-50/50 transition-colors group" data-id="element-6427">
                <FileText className="w-5 h-5 text-slate-400 group-hover:text-purple-600 transition-colors" data-id="element-6428" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-purple-700" data-id="element-6429">
                  Отчёты
                </span>
              </button>
              <button onClick={() => {
            setSelectedUser(null);
            setActiveTab('consultations');
          }} className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/50 transition-colors group" data-id="element-6430">
                <Video className="w-5 h-5 text-slate-400 group-hover:text-teal-600 transition-colors" data-id="element-6431" />
                <span className="text-[11px] font-medium text-slate-600 group-hover:text-teal-700" data-id="element-6432">
                  Консультации ({selectedUser.consultations})
                </span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-6433">
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-6434">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-6435">
                  <Phone className="w-3 h-3" data-id="element-6436" /> Телефон
                </div>
                <div className="text-sm font-medium" data-id="element-6437">{selectedUser.phone}</div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-6438">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-6439">
                  <MapPin className="w-3 h-3" data-id="element-6440" /> Страна
                </div>
                <div className="text-sm font-medium" data-id="element-6441">
                  {selectedUser.country}
                </div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-6442">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-6443">
                  <Calendar className="w-3 h-3" data-id="element-6444" /> Регистрация
                </div>
                <div className="text-sm font-medium" data-id="element-6445">{selectedUser.date}</div>
              </Card>
              <Card padding="sm" className="bg-slate-50 border-none shadow-none" data-id="element-6446">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1" data-id="element-6447">
                  <Clock className="w-3 h-3" data-id="element-6448" /> Последний вход
                </div>
                <div className="text-sm font-medium" data-id="element-6449">
                  {selectedUser.lastLogin}
                </div>
              </Card>
            </div>

            <div className="space-y-4" data-id="element-6450">
              <div className="flex justify-between items-center" data-id="element-6451">
                <h3 className="font-bold text-slate-900" data-id="element-6452">Внутренние заметки</h3>
                <Button variant="ghost" size="sm" icon={<Edit3 className="w-4 h-4" data-id="element-6454" />} onClick={() => setNoteModal({
              userId: selectedUser.id,
              text: selectedUser.notes
            })} data-id="element-6453">
                  Редактировать
                </Button>
              </div>
              {selectedUser.notes ? <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 whitespace-pre-wrap" data-id="element-6455">
                  {selectedUser.notes}
                </div> : <div className="text-sm text-slate-400 italic" data-id="element-6456">Нет заметок</div>}
              <div className="flex justify-between items-center mt-6" data-id="element-6457">
                <h3 className="font-bold text-slate-900" data-id="element-6458">Risk Flag</h3>
                <Button variant="ghost" size="sm" icon={<Edit3 className="w-4 h-4" data-id="element-6460" />} onClick={() => setRiskModal({
              userId: selectedUser.id,
              flag: selectedUser.riskFlag || ''
            })} data-id="element-6459">
                  Редактировать
                </Button>
              </div>
              {selectedUser.riskFlag ? <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex items-start gap-2" data-id="element-6461">
                  <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" data-id="element-6462" />
                  <span className="text-sm text-rose-800 font-medium" data-id="element-6463">
                    {selectedUser.riskFlag}
                  </span>
                </div> : <div className="text-sm text-slate-400 italic" data-id="element-6464">
                  Нет флагов риска
                </div>}
            </div>
          </div>
    }, {
      id: 'orders',
      label: `Заказы (${selectedUser.orders})`,
      content: <div className="space-y-4" data-id="element-6465">
            <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg" data-id="element-6466">
              <span className="text-sm text-slate-600" data-id="element-6467">Всего потрачено:</span>
              <span className="font-bold text-lg text-slate-900" data-id="element-6468">
                {selectedUser.spent}
              </span>
            </div>
            {mockOrders.map(order => <Card key={order.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('orders');
        }} data-id="element-6469">
                <div className="flex justify-between items-center" data-id="element-6470">
                  <div data-id="element-6471">
                    <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-6472">
                      Заказ #ORD-{order.id}
                      <span className="text-indigo-500 text-[10px]" data-id="element-6473">
                        → открыть
                      </span>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-6474">
                      {order.product} • {order.date}
                    </div>
                  </div>
                  <div className="text-right" data-id="element-6475">
                    <div className="font-bold text-sm" data-id="element-6476">
                      {order.price.toLocaleString()} ₽
                    </div>
                    <Badge variant={order.status === 'completed' ? 'success' : 'warning'} size="sm" data-id="element-6477">
                      {order.status === 'completed' ? 'Выполнен' : 'В работе'}
                    </Badge>
                  </div>
                </div>
              </Card>)}
            {mockOrders.length === 0 && <div className="text-center py-8 text-slate-500" data-id="element-6478">Нет заказов</div>}
            {parseInt(selectedUser.orders) > 0 && <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('orders');
        }} className="w-full text-center py-2.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-6479">
                Все заказы пользователя →
              </button>}
          </div>
    }, {
      id: 'reports',
      label: 'Отчёты',
      content: <div className="space-y-4" data-id="element-6480">
            {mockReports.slice(0, parseInt(selectedUser.orders) || 0).map(report => <Card key={report.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-purple-200 hover:bg-purple-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('ai_reports');
        }} data-id="element-6481">
                  <div className="flex justify-between items-center" data-id="element-6482">
                    <div className="flex items-center gap-3" data-id="element-6483">
                      <div className="w-10 h-10 bg-purple-50 rounded-full flex items-center justify-center" data-id="element-6484">
                        <FileText className="w-5 h-5 text-purple-600" data-id="element-6485" />
                      </div>
                      <div data-id="element-6486">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-6487">
                          {report.type}
                          <span className="text-purple-500 text-[10px]" data-id="element-6488">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-6489">
                          {report.date}
                        </div>
                      </div>
                    </div>
                    <Badge variant={report.status === 'ready' ? 'success' : 'warning'} size="sm" data-id="element-6490">
                      {report.status === 'ready' ? 'Готов' : 'Генерация...'}
                    </Badge>
                  </div>
                </Card>)}
            {(parseInt(selectedUser.orders) || 0) === 0 && <div className="text-center py-8 text-slate-500" data-id="element-6491">Нет отчётов</div>}
            <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('ai_reports');
        }} className="w-full text-center py-2.5 text-sm font-medium text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors" data-id="element-6492">
              Все отчёты в системе →
            </button>
          </div>
    }, {
      id: 'consultations',
      label: `Консультации (${selectedUser.consultations})`,
      content: <div className="space-y-4" data-id="element-6493">
            {mockConsultations.slice(0, selectedUser.consultations).map(cons => <Card key={cons.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-teal-200 hover:bg-teal-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('consultations');
        }} data-id="element-6494">
                  <div className="flex justify-between items-center" data-id="element-6495">
                    <div className="flex items-center gap-3" data-id="element-6496">
                      <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center" data-id="element-6497">
                        <Video className="w-5 h-5 text-teal-600" data-id="element-6498" />
                      </div>
                      <div data-id="element-6499">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-6500">
                          {cons.type}
                          <span className="text-teal-500 text-[10px]" data-id="element-6501">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-6502">
                          Астролог: {cons.astrologer} • {cons.duration} •{' '}
                          {cons.date}
                        </div>
                      </div>
                    </div>
                    <Badge variant="neutral" size="sm" data-id="element-6503">
                      Завершена
                    </Badge>
                  </div>
                </Card>)}
            {selectedUser.consultations === 0 && <div className="text-center py-8 text-slate-500" data-id="element-6504">
                Нет консультаций
              </div>}
            {selectedUser.consultations > 0 && <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('consultations');
        }} className="w-full text-center py-2.5 text-sm font-medium text-teal-600 hover:text-teal-800 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-6505">
                Все консультации →
              </button>}
          </div>
    }, {
      id: 'chats',
      label: 'Чаты',
      content: <div className="space-y-4" data-id="element-6506">
            {selectedUser.hasActiveChats ? mockChats.map(chat => <Card key={chat.id} padding="sm" className="border-slate-200 shadow-none cursor-pointer hover:border-blue-200 hover:bg-blue-50/30 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('full_chat');
        }} data-id="element-6507">
                  <div className="flex justify-between items-center" data-id="element-6508">
                    <div className="flex items-center gap-3" data-id="element-6509">
                      <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center" data-id="element-6510">
                        <MessageSquare className="w-5 h-5 text-blue-600" data-id="element-6511" />
                      </div>
                      <div data-id="element-6512">
                        <div className="font-medium text-sm text-slate-900 flex items-center gap-2" data-id="element-6513">
                          {chat.with}
                          <Badge variant="neutral" size="sm" data-id="element-6514">
                            {chat.role}
                          </Badge>
                          <span className="text-blue-500 text-[10px]" data-id="element-6515">
                            → открыть
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 truncate max-w-[250px]" data-id="element-6516">
                          {chat.lastMsg}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end gap-1" data-id="element-6517">
                      <div className="text-[10px] text-slate-400" data-id="element-6518">
                        {chat.time}
                      </div>
                      {chat.unread > 0 && <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-id="element-6519">
                          {chat.unread}
                        </span>}
                    </div>
                  </div>
                </Card>) : <div className="text-center py-8 text-slate-500" data-id="element-6520">
                Нет активных диалогов
              </div>}
            <button onClick={() => {
          setSelectedUser(null);
          setActiveTab('full_chat');
        }} className="w-full text-center py-2.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-6521">
              Открыть чат-центр →
            </button>
          </div>
    }, {
      id: 'complaints',
      label: 'Жалобы',
      content: <div className="space-y-4" data-id="element-6522">
            {selectedUser.hasComplaints ? <Card padding="sm" className="border-rose-100 bg-rose-50 shadow-none cursor-pointer hover:bg-rose-100/50 transition-colors" onClick={() => {
          setSelectedUser(null);
          setActiveTab('moderation');
        }} data-id="element-6523">
                <div className="flex justify-between items-start mb-2" data-id="element-6524">
                  <Badge variant="error" size="sm" data-id="element-6525">
                    Открыта
                  </Badge>
                  <span className="text-xs text-slate-500" data-id="element-6526">Вчера</span>
                </div>
                <div className="font-medium text-sm text-slate-900 mb-1 flex items-center gap-2" data-id="element-6527">
                  Жалоба на астролога (Павел Орион)
                  <span className="text-rose-500 text-[10px]" data-id="element-6528">→ модерация</span>
                </div>
                <p className="text-sm text-slate-700" data-id="element-6529">
                  Астролог опоздал на консультацию на 15 минут и общался грубо.
                  Прошу вернуть деньги.
                </p>
              </Card> : <div className="text-center py-8 text-slate-500" data-id="element-6530">Нет жалоб</div>}
          </div>
    }, {
      id: 'activity',
      label: 'Активность',
      content: <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent" data-id="element-6531">
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-6532">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-indigo-100 text-indigo-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-6533">
                <Clock className="w-4 h-4" data-id="element-6534" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-6535">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-6536">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-6537">
                    Вход в систему
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-6538">
                    {selectedUser.lastLogin}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-6539">
                  IP: 192.168.1.1 • {selectedUser.country}
                </div>
              </div>
            </div>
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-6540">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-emerald-100 text-emerald-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-6541">
                <ShoppingBag className="w-4 h-4" data-id="element-6542" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-emerald-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('orders');
          }} data-id="element-6543">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-6544">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-6545">
                    Оформлен заказ{' '}
                    <span className="text-emerald-500 text-[10px]" data-id="element-6546">→</span>
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-6547">
                    {selectedUser.lastOrder}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-6548">
                  Натальная карта (3 900 ₽)
                </div>
              </div>
            </div>
            {selectedUser.consultations > 0 && <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-6549">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-teal-100 text-teal-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-6550">
                  <Video className="w-4 h-4" data-id="element-6551" />
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-teal-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('consultations');
          }} data-id="element-6552">
                  <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-6553">
                    <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-6554">
                      Консультация{' '}
                      <span className="text-teal-500 text-[10px]" data-id="element-6555">→</span>
                    </div>
                    <time className="text-xs font-medium text-slate-500" data-id="element-6556">
                      05 Окт 2024
                    </time>
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-6557">
                    Видео с Мария Звёздная (45 мин)
                  </div>
                </div>
              </div>}
            <div className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-6558">
              <div className="flex items-center justify-center w-10 h-10 rounded-full border border-white bg-purple-100 text-purple-600 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2" data-id="element-6559">
                <FileText className="w-4 h-4" data-id="element-6560" />
              </div>
              <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-slate-200 bg-white shadow-sm cursor-pointer hover:border-purple-200 transition-colors" onClick={() => {
            setSelectedUser(null);
            setActiveTab('ai_reports');
          }} data-id="element-6561">
                <div className="flex items-center justify-between space-x-2 mb-1" data-id="element-6562">
                  <div className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-6563">
                    Отчёт сгенерирован{' '}
                    <span className="text-purple-500 text-[10px]" data-id="element-6564">→</span>
                  </div>
                  <time className="text-xs font-medium text-slate-500" data-id="element-6565">
                    {selectedUser.lastOrder}
                  </time>
                </div>
                <div className="text-sm text-slate-500" data-id="element-6566">
                  Натальная карта — AI отчёт
                </div>
              </div>
            </div>
          </div>
    }];
    return <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setSelectedUser(null);
    }} data-id="element-6567">
        <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col h-full animate-in slide-in-from-right" data-id="element-6568">
          <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/80 backdrop-blur-md" data-id="element-6569">
            <h3 className="text-base font-bold text-slate-900" data-id="element-6570">
              Карточка пользователя
            </h3>
            <div className="flex items-center gap-2" data-id="element-6571">
              <Button variant="outline" size="sm" icon={<Ban className="w-4 h-4" data-id="element-6573" />} onClick={() => handleToggleBlock(selectedUser.id)} data-id="element-6572">
                {selectedUser.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
              </Button>
              <button className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors" onClick={() => setSelectedUser(null)} data-id="element-6574">
                <X className="w-5 h-5" data-id="element-6575" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-6576">
            <div className="p-6" data-id="element-6577">
              <Tabs tabs={tabs} data-id="element-6578" />
            </div>
          </div>
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-6579">
      <div className="flex justify-between items-center" data-id="element-6580">
        <div data-id="element-6581">
          <h2 className="text-xl font-bold text-slate-900" data-id="element-6582">Пользователи</h2>
          <p className="text-sm text-slate-500" data-id="element-6583">
            Управление клиентами платформы
          </p>
        </div>
        {setCreatePanel && <Button variant="primary" icon={<UserCheck className="w-4 h-4" data-id="element-6585" />} onClick={() => setCreatePanel({
        type: 'user',
        title: 'Добавить пользователя'
      })} data-id="element-6584">
            Добавить
          </Button>}
      </div>

      {/* KPI Cards - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-id="element-6586">
        {renderKpiCard('Всего', kpis.total, <Users className="w-4 h-4 text-indigo-600" data-id="element-6587" />, 'bg-indigo-50', 'text-indigo-600')}
        {renderKpiCard('Новые (сегодня)', kpis.newToday, <Users className="w-4 h-4 text-emerald-600" data-id="element-6588" />, 'bg-emerald-50', 'text-emerald-600')}
        {renderKpiCard('Активные', kpis.active, <Activity className="w-4 h-4 text-blue-600" data-id="element-6589" />, 'bg-blue-50', 'text-blue-600', 'active')}
        {renderKpiCard('Заблокированные', kpis.blocked, <Ban className="w-4 h-4 text-rose-600" data-id="element-6590" />, 'bg-rose-50', 'text-rose-600', 'blocked')}
        {renderKpiCard('VIP клиенты', kpis.vip, <Star className="w-4 h-4 text-amber-600" data-id="element-6591" />, 'bg-amber-50', 'text-amber-600', 'vip')}
      </div>

      {/* KPI Cards - Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-id="element-6592">
        {renderKpiCard('С подпиской', kpis.withSub, <CreditCard className="w-4 h-4 text-purple-600" data-id="element-6593" />, 'bg-purple-50', 'text-purple-600', 'sub')}
        {renderKpiCard('С жалобами', kpis.withComplaints, <AlertTriangle className="w-4 h-4 text-orange-600" data-id="element-6594" />, 'bg-orange-50', 'text-orange-600', 'complaints')}
        {renderKpiCard('Ждут заказ', kpis.pendingOrders, <Clock className="w-4 h-4 text-sky-600" data-id="element-6595" />, 'bg-sky-50', 'text-sky-600', 'pendingOrders')}
        {renderKpiCard('Активные конс.', kpis.activeConsultations, <Video className="w-4 h-4 text-teal-600" data-id="element-6596" />, 'bg-teal-50', 'text-teal-600', 'consultations')}
        {renderKpiCard('Возвраты', kpis.refunds, <RefreshCw className="w-4 h-4 text-red-600" data-id="element-6597" />, 'bg-red-50', 'text-red-600', 'refunds')}
      </div>

      {/* Filter Bar */}
      <Card padding="sm" className="rounded-xl shadow-none border-slate-200" data-id="element-6598">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-6599">
          <div className="flex items-center gap-3 w-full sm:w-auto flex-1" data-id="element-6600">
            <div className="relative flex-1 max-w-md" data-id="element-6601">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-6602" />
              <input type="text" placeholder="Поиск по имени, email, телефону, ID..." className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6603" />
            </div>
            <Button variant={showFilters ? 'primary' : 'outline'} size="sm" icon={<Filter className="w-4 h-4" data-id="element-6605" />} onClick={() => setShowFilters(!showFilters)} data-id="element-6604">
              Фильтры
            </Button>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-6606">
            <Button variant="outline" size="sm" icon={<Download className="w-4 h-4" data-id="element-6608" />} onClick={handleExport} data-id="element-6607">
              Экспорт CSV
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showFilters && <div className="mt-4 pt-4 border-t border-slate-100 space-y-4 animate-in slide-in-from-top-2" data-id="element-6609">
            {/* Row 1: Selects */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-6610">
              <div data-id="element-6611">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6612">
                  Статус
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.status} onChange={e => setFilters({
              ...filters,
              status: e.target.value
            })} data-id="element-6613">
                  <option value="all" data-id="element-6614">Все</option>
                  <option value="active" data-id="element-6615">Активные</option>
                  <option value="blocked" data-id="element-6616">Заблокированные</option>
                </select>
              </div>
              <div data-id="element-6617">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6618">
                  Подписка
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.subscription} onChange={e => setFilters({
              ...filters,
              subscription: e.target.value
            })} data-id="element-6619">
                  <option value="all" data-id="element-6620">Все</option>
                  <option value="true" data-id="element-6621">Есть подписка</option>
                  <option value="false" data-id="element-6622">Нет подписки</option>
                </select>
              </div>
              <div data-id="element-6623">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6624">
                  Страна
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.country} onChange={e => setFilters({
              ...filters,
              country: e.target.value
            })} data-id="element-6625">
                  <option value="all" data-id="element-6626">Все</option>
                  <option value="Россия" data-id="element-6627">Россия</option>
                  <option value="Беларусь" data-id="element-6628">Беларусь</option>
                  <option value="Казахстан" data-id="element-6629">Казахстан</option>
                  <option value="Украина" data-id="element-6630">Украина</option>
                </select>
              </div>
              <div data-id="element-6631">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6632">
                  Жалобы
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.hasComplaints} onChange={e => setFilters({
              ...filters,
              hasComplaints: e.target.value
            })} data-id="element-6633">
                  <option value="all" data-id="element-6634">Все</option>
                  <option value="true" data-id="element-6635">Есть жалобы</option>
                  <option value="false" data-id="element-6636">Нет жалоб</option>
                </select>
              </div>
            </div>

            {/* Row 2: Consultations + Number ranges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-6637">
              <div data-id="element-6638">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6639">
                  Консультации
                </label>
                <select className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" value={filters.hasConsultations} onChange={e => setFilters({
              ...filters,
              hasConsultations: e.target.value
            })} data-id="element-6640">
                  <option value="all" data-id="element-6641">Все</option>
                  <option value="true" data-id="element-6642">Были консультации</option>
                  <option value="false" data-id="element-6643">Не было</option>
                </select>
              </div>
              <div data-id="element-6644">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6645">
                  Заказов
                </label>
                <div className="flex gap-2" data-id="element-6646">
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="от" value={filters.ordersMin} onChange={e => setFilters({
                ...filters,
                ordersMin: e.target.value
              })} data-id="element-6647" />
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="до" value={filters.ordersMax} onChange={e => setFilters({
                ...filters,
                ordersMax: e.target.value
              })} data-id="element-6648" />
                </div>
              </div>
              <div data-id="element-6649">
                <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-6650">
                  Потрачено (₽)
                </label>
                <div className="flex gap-2" data-id="element-6651">
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="от" value={filters.spentMin} onChange={e => setFilters({
                ...filters,
                spentMin: e.target.value
              })} data-id="element-6652" />
                  <input type="number" className="w-full rounded-lg border border-slate-200 bg-white text-sm py-2 px-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" placeholder="до" value={filters.spentMax} onChange={e => setFilters({
                ...filters,
                spentMax: e.target.value
              })} data-id="element-6653" />
                </div>
              </div>
              <div className="flex items-end" data-id="element-6654">
                <Button variant="ghost" size="sm" onClick={() => setFilters({
              status: 'all',
              subscription: 'all',
              ordersMin: '',
              ordersMax: '',
              spentMin: '',
              spentMax: '',
              dateFrom: '',
              dateTo: '',
              country: 'all',
              language: 'all',
              hasComplaints: 'all',
              hasActiveOrders: 'all',
              hasConsultations: 'all'
            })} data-id="element-6655">
                  Сбросить
                </Button>
              </div>
            </div>
          </div>}
      </Card>
      {/* Mass Actions Bar */}
      {selectedUsers.size > 0 && <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 flex items-center justify-between animate-in fade-in" data-id="element-6656">
          <div className="flex items-center gap-3" data-id="element-6657">
            <Badge variant="pro" data-id="element-6658">{selectedUsers.size} выбрано</Badge>
            <span className="text-sm text-indigo-800 font-medium" data-id="element-6659">
              Массовые действия:
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-6660">
            <Button variant="outline" size="sm" className="bg-white" icon={<Mail className="w-4 h-4" data-id="element-6662" />} onClick={() => handleMassAction('email')} data-id="element-6661">
              Email
            </Button>
            <Button variant="outline" size="sm" className="bg-white" icon={<Tag className="w-4 h-4" data-id="element-6664" />} onClick={() => handleMassAction('tag')} data-id="element-6663">
              Тег
            </Button>
            <Button variant="outline" size="sm" className="bg-white" icon={<Download className="w-4 h-4" data-id="element-6666" />} onClick={() => handleMassAction('export')} data-id="element-6665">
              Экспорт
            </Button>
            <Button variant="outline" size="sm" className="bg-white text-rose-600 border-rose-200 hover:bg-rose-50" icon={<Ban className="w-4 h-4" data-id="element-6668" />} onClick={() => handleMassAction('block')} data-id="element-6667">
              Заблокировать
            </Button>
          </div>
        </div>}
      {/* Data Table */}
      <Card padding="none" className="rounded-xl shadow-none border-slate-200 overflow-hidden" data-id="element-6669">
        <div className="overflow-x-auto" data-id="element-6670">
          <table className="w-full text-left" data-id="element-6671">
            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[11px] uppercase tracking-wider" data-id="element-6672">
              <tr data-id="element-6673">
                <th className="px-4 py-3 w-10" data-id="element-6674">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-indigo-600" data-id="element-6675">
                    {selectedUsers.size === currentUsers.length && currentUsers.length > 0 ? <CheckSquare className="w-4 h-4" data-id="element-6676" /> : <Square className="w-4 h-4" data-id="element-6677" />}
                  </button>
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-6678">Пользователь</th>
                <th className="px-4 py-3 font-medium" data-id="element-6679">Регистрация / Вход</th>
                <th className="px-4 py-3 font-medium" data-id="element-6680">Страна</th>
                <th className="px-4 py-3 font-medium" data-id="element-6681">Активность</th>
                <th className="px-4 py-3 font-medium" data-id="element-6682">Потрачено</th>
                <th className="px-4 py-3 font-medium" data-id="element-6683">Статус</th>
                <th className="px-4 py-3 text-right font-medium" data-id="element-6684">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-6685">
              {currentUsers.map(user => <tr key={user.id} className={`hover:bg-slate-50 transition-colors group ${selectedUsers.has(user.id) ? 'bg-indigo-50/30' : ''}`} data-id="element-6686">
                  <td className="px-4 py-3" data-id="element-6687">
                    <button onClick={() => toggleSelectUser(user.id)} className={`hover:text-indigo-600 ${selectedUsers.has(user.id) ? 'text-indigo-600' : 'text-slate-300'}`} data-id="element-6688">
                      {selectedUsers.has(user.id) ? <CheckSquare className="w-4 h-4" data-id="element-6689" /> : <Square className="w-4 h-4" data-id="element-6690" />}
                    </button>
                  </td>
                  <td className="px-4 py-3" data-id="element-6691">
                    <div className="flex items-center gap-3" data-id="element-6692">
                      <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs shrink-0" data-id="element-6693">
                        {user.name.split(' ').map((n: string) => n[0]).join('')}
                      </div>
                      <div className="min-w-0" data-id="element-6694">
                        <div className="text-sm font-medium text-slate-900 group-hover:text-indigo-600 cursor-pointer truncate flex items-center gap-1" onClick={() => setSelectedUser(user)} data-id="element-6695">
                          {user.name}
                          {user.isVip && <Star className="w-3 h-3 text-amber-500 fill-amber-500 shrink-0" title="VIP клиент" data-id="element-6696" />}
                        </div>
                        <div className="text-xs text-slate-500 truncate" data-id="element-6697">
                          {user.email}
                        </div>

                        {/* Indicators Row */}
                        <div className="flex items-center gap-1.5 mt-1" data-id="element-6698">
                          {user.hasActiveChats && <span className="relative group/tip cursor-pointer" data-id="element-6699">
                              <MessageSquare className="w-3.5 h-3.5 text-blue-500" data-id="element-6700" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-6701">
                                💬 Есть активные чаты
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-6702" />
                              </span>
                            </span>}
                          {user.hasComplaints && <span className="relative group/tip cursor-pointer" data-id="element-6703">
                              <AlertTriangle className="w-3.5 h-3.5 text-orange-500" data-id="element-6704" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-6705">
                                ⚠️ Есть жалобы на пользователя
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-6706" />
                              </span>
                            </span>}
                          {user.hasPaymentIssues && <span className="relative group/tip cursor-pointer" data-id="element-6707">
                              <CreditCard className="w-3.5 h-3.5 text-rose-500" data-id="element-6708" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-6709">
                                💳 Проблемы с оплатой
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-6710" />
                              </span>
                            </span>}
                          {user.riskFlag && <span className="relative group/tip cursor-pointer" data-id="element-6711">
                              <AlertTriangle className="w-3.5 h-3.5 text-rose-600" data-id="element-6712" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-rose-700 text-white text-[10px] font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-6713">
                                🚨 Risk: {user.riskFlag}
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-rose-700" data-id="element-6714" />
                              </span>
                            </span>}
                          {user.notes && <span className="relative group/tip cursor-pointer" data-id="element-6715">
                              <FileText className="w-3.5 h-3.5 text-amber-500" data-id="element-6716" />
                              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 bg-slate-800 text-white text-[10px] font-medium rounded-lg max-w-[200px] truncate opacity-0 invisible group-hover/tip:opacity-100 group-hover/tip:visible transition-all duration-150 pointer-events-none z-30 shadow-lg" data-id="element-6717">
                                📝 {user.notes || 'Есть заметка'}
                                <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-6718" />
                              </span>
                            </span>}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-6719">
                    <div className="text-xs text-slate-900" data-id="element-6720">{user.date}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-6721">
                      Вход: {user.lastLogin}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-id="element-6722">
                    {user.country}
                  </td>
                  <td className="px-4 py-3" data-id="element-6723">
                    <div className="flex gap-3" data-id="element-6724">
                      <div className="text-center" title="Заказы" data-id="element-6725">
                        <div className="text-xs font-bold text-slate-900" data-id="element-6726">
                          {user.orders}
                        </div>
                        <div className="text-[9px] text-slate-500 uppercase" data-id="element-6727">
                          Зак
                        </div>
                      </div>
                      <div className="text-center" title="Консультации" data-id="element-6728">
                        <div className="text-xs font-bold text-slate-900" data-id="element-6729">
                          {user.consultations}
                        </div>
                        <div className="text-[9px] text-slate-500 uppercase" data-id="element-6730">
                          Конс
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-id="element-6731">
                    <div className="text-sm font-medium text-slate-900" data-id="element-6732">
                      {user.spent}
                    </div>
                    {user.subscription && <Badge variant={user.subscription === 'Pro' ? 'pro' : 'featured'} size="sm" className="mt-1" data-id="element-6733">
                        {user.subscription}
                      </Badge>}
                  </td>
                  <td className="px-4 py-3" data-id="element-6734">
                    <Badge variant={user.status === 'active' ? 'success' : 'error'} size="sm" data-id="element-6735">
                      {user.status === 'active' ? 'Активен' : 'Заблокирован'}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right relative" data-id="element-6736">
                    <Button variant="ghost" size="sm" className="w-8 h-8 p-0 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" icon={<MoreVertical className="w-4 h-4" data-id="element-6738" />} onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)} data-id="element-6737" />

                    {/* Action Dropdown */}
                    {actionMenuOpen === user.id && <>
                        <div className="fixed inset-0 z-10" onClick={() => setActionMenuOpen(null)} data-id="element-6739" />
                        <div className="absolute right-8 top-10 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-1 z-20 text-left animate-in fade-in zoom-in-95" data-id="element-6740">
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setSelectedUser(user);
                      setActionMenuOpen(null);
                    }} data-id="element-6741">
                            <Eye className="w-3.5 h-3.5" data-id="element-6742" /> Открыть профиль
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('orders');
                      setActionMenuOpen(null);
                    }} data-id="element-6743">
                            <ShoppingBag className="w-3.5 h-3.5" data-id="element-6744" /> Открыть
                            заказы
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('full_chat');
                      setActionMenuOpen(null);
                    }} data-id="element-6745">
                            <MessageSquare className="w-3.5 h-3.5" data-id="element-6746" /> Открыть
                            чаты
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('ai_reports');
                      setActionMenuOpen(null);
                    }} data-id="element-6747">
                            <Zap className="w-3.5 h-3.5" data-id="element-6748" /> Открыть отчёты
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setActiveTab('consultations');
                      setActionMenuOpen(null);
                    }} data-id="element-6749">
                            <Video className="w-3.5 h-3.5" data-id="element-6750" /> Консультации
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-6751"></div>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setNoteModal({
                        userId: user.id,
                        text: user.notes
                      });
                      setActionMenuOpen(null);
                    }} data-id="element-6752">
                            <Edit3 className="w-3.5 h-3.5" data-id="element-6753" /> Добавить заметку
                          </button>
                          <button className="w-full px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2" onClick={() => {
                      setRiskModal({
                        userId: user.id,
                        flag: user.riskFlag || ''
                      });
                      setActionMenuOpen(null);
                    }} data-id="element-6754">
                            <AlertTriangle className="w-3.5 h-3.5" data-id="element-6755" /> Risk-флаг
                          </button>
                          <div className="h-px bg-slate-100 my-1" data-id="element-6756"></div>
                          <button className="w-full px-4 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2" onClick={() => handleToggleBlock(user.id)} data-id="element-6757">
                            {user.status === 'active' ? <Ban className="w-3.5 h-3.5" data-id="element-6758" /> : <UserCheck className="w-3.5 h-3.5" data-id="element-6759" />}
                            {user.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
                          </button>
                        </div>
                      </>}
                  </td>
                </tr>)}
              {currentUsers.length === 0 && <tr data-id="element-6760">
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500" data-id="element-6761">
                    Пользователи не найдены по заданным фильтрам
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && <div className="p-4 border-t border-slate-100 flex justify-center" data-id="element-6762">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-6763" />
          </div>}
      </Card>

      {/* Detail Panel */}
      {renderUserDetailPanel()}
      {/* Note Modal */}
      {noteModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setNoteModal(null);
    }} data-id="element-6764">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6" data-id="element-6765">
            <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-6766">
              Внутренняя заметка
            </h3>
            <p className="text-xs text-slate-500 mb-4" data-id="element-6767">
              Эта заметка видна только администраторам.
            </p>
            <textarea className="w-full border border-slate-200 rounded-lg p-3 text-sm min-h-[120px] focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 mb-4" placeholder="Например: VIP клиент, часто жалуется..." value={noteModal.text} onChange={e => setNoteModal({
          ...noteModal,
          text: e.target.value
        })} data-id="element-6768" />
            <div className="flex justify-end gap-3" data-id="element-6769">
              <Button variant="ghost" onClick={() => setNoteModal(null)} data-id="element-6770">
                Отмена
              </Button>
              <Button variant="primary" onClick={handleSaveNote} data-id="element-6771">
                Сохранить
              </Button>
            </div>
          </div>
        </div>}
      {/* Risk Flag Modal */}
      {riskModal && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm" onClick={e => {
      if (e.target === e.currentTarget) setRiskModal(null);
    }} data-id="element-6772">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6" data-id="element-6773">
            <div className="flex items-center gap-2 mb-4" data-id="element-6774">
              <AlertTriangle className="w-5 h-5 text-rose-600" data-id="element-6775" />
              <h3 className="text-lg font-bold text-slate-900" data-id="element-6776">Risk Flag</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4" data-id="element-6777">
              Установите флаг риска для привлечения внимания модераторов.
            </p>
            <Input label="Причина риска" placeholder="Например: подозрение на мошенничество" value={riskModal.flag} onChange={e => setRiskModal({
          ...riskModal,
          flag: e.target.value
        })} data-id="element-6778" />
            <div className="flex gap-2 mt-3 mb-6" data-id="element-6779">
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Конфликтный'
          })} data-id="element-6780">
                Конфликтный
              </button>
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Передача контактов'
          })} data-id="element-6781">
                Передача контактов
              </button>
              <button className="text-[10px] bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded text-slate-600" onClick={() => setRiskModal({
            ...riskModal,
            flag: 'Мошенничество'
          })} data-id="element-6782">
                Мошенничество
              </button>
            </div>
            <div className="flex justify-between items-center" data-id="element-6783">
              <Button variant="ghost" className="text-rose-600" onClick={() => {
            setRiskModal({
              ...riskModal,
              flag: ''
            });
            handleSaveRisk();
          }} data-id="element-6784">
                Снять флаг
              </Button>
              <div className="flex gap-3" data-id="element-6785">
                <Button variant="ghost" onClick={() => setRiskModal(null)} data-id="element-6786">
                  Отмена
                </Button>
                <Button variant="primary" onClick={handleSaveRisk} data-id="element-6787">
                  Сохранить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}